import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import MarketingLanding from "./pages/MarketingLanding";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import Onboarding from "./pages/Onboarding";
import MainHub from "./pages/MainHub";
import Dashboard from "./pages/Dashboard";
import WorkoutPlan from "./pages/WorkoutPlan";
import LogWorkout from "./pages/LogWorkout";
import LogActivity from "./pages/LogActivity";
import Progress from "./pages/Progress";
import ProgressPhotos from "./pages/ProgressPhotos";
import Activity from "./pages/Activity";
import Nutrition from "./pages/Nutrition";
import PreparedMeals from "./pages/PreparedMeals";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import PaymentSuccess from "./pages/PaymentSuccess";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import BattleArena from "./pages/BattleArena";
import FindPeople from "./pages/FindPeople";
import Friends from "./pages/Friends";
import ActiveRecovery from "./pages/ActiveRecovery";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Armory from "./pages/Armory";
import ExerciseGallery from "./pages/ExerciseGallery";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PrivacySharing from "./pages/PrivacySharing";
import TeamsHub from "./pages/TeamsHub";
import FamilyHub from "./pages/FamilyHub";
import TeamDetailView from "./pages/coach/TeamDetailView";
import AthleteDetailView from "./pages/coach/AthleteDetailView";
import PlanBuilder from "./pages/coach/PlanBuilder";
import JoinOrg from "./pages/JoinOrg";
import TermsOfService from "./pages/TermsOfService";
import DisclaimerModal from "./components/DisclaimerModal";
import InstallPrompt from "./components/InstallPrompt";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Helper function to get LOCAL date string (YYYY-MM-DD) without timezone issues
export const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to get local datetime ISO string (preserves local time)
export const getLocalISOString = (date = new Date()) => {
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().slice(0, -1);
};

// Helper function to strip HTML tags from text
export const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  let text = tmp.textContent || tmp.innerText || '';
  text = text.replace(/\s+/g, ' ').trim();
  return text;
};

// Set up axios interceptor to include auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workoutType, setWorkoutType] = useState(() => {
    return localStorage.getItem('workoutType') || null;
  });
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    if (workoutType) {
      localStorage.setItem('workoutType', workoutType);
    }
  }, [workoutType]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        if (!response.data.disclaimer_accepted) {
          setShowDisclaimer(true);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  const handleDisclaimerAccept = () => {
    setShowDisclaimer(false);
    if (user) {
      const updatedUser = { ...user, disclaimer_accepted: true };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    try { localStorage.setItem('aa_returning', '1'); } catch (e) { /* noop */ }
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('workoutType');
    setUser(null);
    setWorkoutType(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="grain-overlay" />
      <BrowserRouter>
        <Routes>
          {/* ─── PUBLIC: Marketing Landing Page (first-time only) ─── */}
          <Route
            path="/"
            element={
              user ? (
                user.onboarding_completed || user.is_org_staff ? (
                  <Navigate to={user.is_org_staff ? "/teams" : "/hub"} replace />
                ) : (
                  <Navigate to="/onboarding" replace />
                )
              ) : localStorage.getItem('aa_returning') ? (
                <Navigate to="/auth" replace />
              ) : (
                <MarketingLanding />
              )
            }
          />

          {/* ─── APP ENTRY: Redirects based on auth state ─── */}
          <Route
            path="/app"
            element={
              user ? (
                user.onboarding_completed || user.is_org_staff ? (
                  <Navigate to={user.is_org_staff ? "/teams" : "/hub"} replace />
                ) : (
                  <Navigate to="/onboarding" replace />
                )
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />

          {/* ─── AUTH ─── */}
          <Route
            path="/auth"
            element={
              user ? (
                localStorage.getItem('pending_invite_code') ? (
                  <Navigate to={`/join/${localStorage.getItem('pending_invite_code')}`} replace />
                ) : localStorage.getItem('post_auth_redirect') ? (
                  <Navigate to={localStorage.getItem('post_auth_redirect')} replace />
                ) : user.is_org_staff ? (
                  <Navigate to="/teams" replace />
                ) : user.onboarding_completed ? (
                  <Navigate to="/hub" replace />
                ) : (
                  <Navigate to="/onboarding" replace />
                )
              ) : (
                <AuthPage onLogin={handleLogin} />
              )
            }
          />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* ─── ONBOARDING ─── */}
          <Route
            path="/onboarding"
            element={
              user ? (
                user.onboarding_completed ? (
                  <Navigate to="/hub" replace />
                ) : (
                  <Onboarding onComplete={() => { checkAuth(); }} onLogout={handleLogout} />
                )
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />

          {/* ─── MAIN APP ROUTES ─── */}
          <Route
            path="/hub"
            element={
              user ? (
                user.onboarding_completed ? (
                  <MainHub user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/onboarding" replace />
                )
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/workout-selection"
            element={user ? <LandingPage onSelectWorkoutType={setWorkoutType} /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/dashboard"
            element={
              user && workoutType ? (
                <Dashboard user={user} workoutType={workoutType} onLogout={handleLogout} onChangeWorkoutType={() => setWorkoutType(null)} />
              ) : user ? (
                <Navigate to="/workout-selection" replace />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/workout-plan"
            element={user && workoutType ? <WorkoutPlan user={user} workoutType={workoutType} onLogout={handleLogout} /> : <Navigate to="/hub" replace />}
          />
          <Route
            path="/log-workout"
            element={
              user ? (
                <LogWorkout
                  user={user}
                  workoutType={workoutType}
                  onLogout={handleLogout}
                />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route path="/progress" element={user ? <Progress user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />} />
          <Route path="/log-activity" element={user ? <LogActivity user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />} />
          <Route path="/battle-arena" element={user ? <BattleArena user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />} />
          <Route path="/armory" element={user ? <Armory /> : <Navigate to="/auth" replace />} />
          <Route path="/active-recovery" element={user ? <ActiveRecovery user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />} />
          <Route path="/find-people" element={user ? <FindPeople user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />} />
          {/* Legacy alias — keep old shared links working */}
          <Route path="/find-warriors" element={<Navigate to="/find-people" replace />} />
          <Route path="/friends" element={user ? <Friends user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />} />
          <Route path="/teams" element={user ? <TeamsHub user={user} /> : <Navigate to="/auth" replace />} />
          <Route path="/privacy-sharing/:orgId" element={user ? <PrivacySharing /> : <Navigate to="/auth" replace />} />
          <Route path="/family" element={user ? <FamilyHub /> : <Navigate to="/auth" replace />} />
          <Route path="/teams/team/:orgId/:teamId" element={user ? <TeamDetailView /> : <Navigate to="/auth" replace />} />
          <Route path="/teams/athlete/:orgId/:athleteId" element={user ? <AthleteDetailView /> : <Navigate to="/auth" replace />} />
          <Route path="/teams/plan-builder/:orgId" element={user ? <PlanBuilder /> : <Navigate to="/auth" replace />} />
          <Route path="/join/:code" element={<JoinOrg user={user} />} />
          <Route path="/activity" element={user ? <Activity user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />} />
          <Route path="/progress-photos" element={user ? <ProgressPhotos user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />} />
          <Route path="/nutrition" element={user ? <Nutrition user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />} />
          <Route path="/prepared-meals" element={user ? <PreparedMeals user={user} /> : <Navigate to="/auth" replace />} />
          <Route path="/settings" element={user ? <Settings user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />} />
          <Route path="/admin" element={user && user.is_admin ? <Admin /> : <Navigate to="/auth" replace />} />
          <Route path="/exercise-gallery" element={user && user.is_admin ? <ExerciseGallery /> : <Navigate to="/auth" replace />} />
          <Route path="/payment-success" element={user ? <PaymentSuccess /> : <Navigate to="/auth" replace />} />
          <Route path="/subscription-success" element={user ? <SubscriptionSuccess /> : <Navigate to="/auth" replace />} />

          {/* ─── PUBLIC PAGES ─── */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <InstallPrompt />
      {showDisclaimer && user && (
        <DisclaimerModal onAccept={handleDisclaimerAccept} />
      )}
    </div>
  );
}

export default App;
